import {ElevationApp} from "../../ElevationApp";
import {MdDoorHanding, MdFrameProfileType, MdFrameSpec} from "../../model/frame/MdFrameSpec";
import {GlazeSide} from "../../shared-2d/CellFill";
import {FrameProfileSTD} from "./profiles/FrameProfileSTD";
import {FrameProfileCO} from "./profiles/FrameProfileCO";
import {FrameProfileDE} from "./profiles/FrameProfileDE";
import {FrameProfileDW} from "./profiles/FrameProfileDW";
import {FrameProfileSL} from "./profiles/FrameProfileSL";
import {FrameProfileSR} from "./profiles/FrameProfileSR";
import {FrameProfileKerf} from "./profiles/FrameProfileKerf";
import {FrameProfileEXP} from "./profiles/FrameProfileEXP";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {FrmRefPoint} from "../../drawfrm/FrmRefPoint";
import {Label} from "../../shared-2d/Label";
import {FrameProfileFF} from "./profiles/FrameProfileFF";
import {FrameProfileTB} from "./profiles/FrameProfileTB";
import {ChildView} from "../../shared-2d/ChildView";
import {inches} from "../../const";
import {FrmVar} from "../../drawfrm/FrmVar";
import {MdFrameProfileDetail} from "../../model/frame/MdFrameProfileDetail";
import {FrameProfileArgs} from "./profiles/ProfileArgs";

export type TProfile = DrawFrmThing & {
    primaryDoorAnchor: FrmRefPoint;
    // a second mounting rabbet, present only on a symmetric double-rabbet profile (STD's
    // other side of the centre stop). Lets an item mount on either rabbet; see
    // balancedMountAnchors.
    secondaryDoorAnchor?: FrmRefPoint;
    // top of the outer face, used to align the two sides of a clamshell mullion.
    faceAnchor: FrmRefPoint;
}

// the frame profiles getProfileObj can actually render (the others throw). Keep in
// sync with the switch in getProfileObj below.
export const supportedProfiles: MdFrameProfileType[] = [
    "STD",
    "STD 2 5/8\"",
    "FF",
    "CO",
    "DE",
    "DW",
    "SL",
    "SR",
    "KF",
    "EXP",
    "TB",
];

// DE/DW/SL/SR draw with the opposite invertY convention to every other profile (they
// invertY on swingIn, the rest on !swingIn), so two profiles from different groups
// render vertically flipped relative to each other. A clamshell mullion mixing groups
// must flip one side (see FrameProfileScene's flipY). Keep in sync with the invertY
// lines in profiles/*.
const INVERTED_Y_PROFILES = new Set<MdFrameProfileType>(["DE", "DW", "SL", "SR"]);

export function profilesShareYOrientation(a: MdFrameProfileType, b: MdFrameProfileType): boolean {
    return INVERTED_Y_PROFILES.has(a) === INVERTED_Y_PROFILES.has(b);
}

export function getProfileObj(props: {
    elev: ElevationApp;
    view: ChildView;
    side: "left" | "right";
    direction: "in" | "out" | "both";
    // the profile cross-section to draw + the two frame-wide dims, passed in by the
    // scene so the profile drawings don't reach into MdFrameSpec themselves.
    detail: MdFrameProfileDetail;
    jambDepth: FrmVar;
    jambFace: FrmVar;
    // which profile shape to draw. Defaults to the frame's own profile; the frame
    // profile view overrides it for a mullion's lite side (that cell's profile).
    profile?: MdFrameProfileType;
    // draw the end returns/hooks pen-up so two profiles merge as a clamshell mullion.
    clamshell?: boolean;
    // clamshell secondary side: also hide the top/bottom faces (the other side caps).
    hideFaces?: boolean;
    // keep the faces but hide their face / depth dimensions (an angled mullion's
    // secondary half).
    hideFaceDims?: boolean;
    // toggle the invertY decision so a clamshell's two halves share one orientation.
    flipY?: boolean;
}): TProfile {
    const elev = props.elev;

    const args: FrameProfileArgs = {
        detail: props.detail,
        jambDepth: props.jambDepth,
        jambFace: props.jambFace,
        view: props.view,
        app: elev.draw,
        left: props.side === "left",
        swingIn: props.direction === "in",
        clamshell: props.clamshell,
        hideFaces: props.hideFaces,
        hideFaceDims: props.hideFaceDims,
        flipY: props.flipY,
    };

    const profile = buildProfile((props.profile ?? elev.frameSpec.profile) as MdFrameProfileType, args, elev);

    // a profile's anchors all sit on the one shape, so a link through any of them
    // (a cell on the secondary rabbet, the clamshell join on the primary) must move
    // the whole profile and carry every other anchor's dependents with it.
    FrmRefPoint.groupExisting([...mountAnchors(profile), profile.faceAnchor]);

    return profile;
}

function buildProfile(type: MdFrameProfileType, args: FrameProfileArgs, elev: ElevationApp): TProfile {
    switch(type) {
        // @ts-ignore
        case "":
            // fall through
        case "STD":
        case "STD 2 5/8\"":
            return new FrameProfileSTD(args);
        case "CO":
            return new FrameProfileCO(args);
        case "DE":
            return new FrameProfileDE(args);
        case "DW":
            return new FrameProfileDW(args);
        case "SL":
            return new FrameProfileSL(args);
        case "SR":
            return new FrameProfileSR(args);
        case "KF":
            return new FrameProfileKerf(args);
        case "EXP":
            return new FrameProfileEXP(args);
        case "FF":
            return new FrameProfileFF(args);
        case "TB":
            return new FrameProfileTB(args);
        case "HS":
        case "PO":
        case "COM":
        default:
            throw new Error("Unknown frame profile: " + elev.frameSpec.profile);
    }
}

// the two faces of a door as the Locations view shows them: "front" looks at the
// opening from its "from" side, "back" from its "to" side.
export type OpeningFace = "front" | "back";

// a side of the opening as the opening names them: it reads "one <transition> two", so
// the from side is location two when the transition is "from", else location one.
export type OpeningSide = "from" | "to";

export function sideLocation(frame: MdFrameSpec, side: OpeningSide): "one" | "two" {
    const fromSide = frame.locationTransition === "from" ? "two" : "one";
    const toSide = fromSide === "one" ? "two" : "one";
    return side === "from" ? fromSide : toSide;
}

export function locationSide(frame: MdFrameSpec, location: "one" | "two"): OpeningSide {
    return sideLocation(frame, "from") === location ? "from" : "to";
}

// which location (one / two) a face looks from: the front from the from side, the back
// from the to side.
export function faceSide(elev: ElevationApp, face: OpeningFace): "one" | "two" {
    return sideLocation(elev.frameSpec, faceOpeningSide(face));
}

export function faceOpeningSide(face: OpeningFace): OpeningSide {
    return face === "front" ? "from" : "to";
}

// the face of a leaf as its hardware knows it: the push face is the one the leaf swings
// away from, the pull face the one it swings toward.
export type DoorFace = "push" | "pull";

// the face of a leaf a viewer on one side of the opening sees, from the handing (which
// way the leaf swings, see swingSideFor) and which location that side is. "out" swings
// toward location one. null for a leaf with no single swing (double-acting, or a
// handing that slides/folds): both its faces take any hardware.
export function doorFace(frame: MdFrameSpec, side: OpeningSide, active: boolean): DoorFace | null {
    let direction: "in" | "out" | "both";
    try {
        direction = swingSideFor(frame, active).direction;
    } catch {
        return null;
    }
    if(direction === "both") return null;

    const swingsToward = direction === "out" ? "one" : "two";
    return sideLocation(frame, side) === swingsToward ? "pull" : "push";
}

// doorFace for one of the Locations view's faces.
export function doorFaceOf(elev: ElevationApp, face: OpeningFace, active: boolean): DoorFace | null {
    return doorFace(elev.frameSpec, faceOpeningSide(face), active);
}

// doorFace for the elevations drawn as seen from location one (the screen and door
// elevations).
export function doorFaceFromLocationOne(elev: ElevationApp, active: boolean): DoorFace | null {
    return doorFace(elev.frameSpec, locationSide(elev.frameSpec, "one"), active);
}

// whether a face shows the door mirrored left-to-right. The elevation views are drawn
// as seen from location one (see TopDownScene: "out" swings toward that side), so a
// face looking from location two is the mirror image.
export function faceMirrored(elev: ElevationApp, face: OpeningFace): boolean {
    return faceSide(elev, face) === "two";
}

// whether the leaf swings toward the viewer of a face. "out" is toward location one;
// a double-acting leaf swings toward both.
export function faceSwingsOut(elev: ElevationApp, face: OpeningFace, active: boolean): boolean {
    let direction: "in" | "out" | "both";
    try {
        direction = getSwingSide(elev, active).direction;
    } catch {
        direction = "out";
    }
    if(direction === "both") return true;
    return faceSide(elev, face) === "one" ? direction === "out" : direction === "in";
}

// the caption over a face: which face it is and the location it's seen from.
export function faceCaption(elev: ElevationApp, face: OpeningFace): string {
    const side = faceSide(elev, face);
    const location = (side === "one" ? elev.opening?.locationOne : elev.opening?.locationTwo) || "location-" + side;
    return face === "front" ? `Front (from ${location})` : `Back (to ${location})`;
}

export function openingToFromLabel(elev: ElevationApp, side: "one" | "two", small: boolean = false) {
    return new Label({
        text: (side === "one" ? elev.opening?.locationOne : elev.opening?.locationTwo) || "location-" + side,
        fontSize: small ? 14 : 20,
    });
}

export function linkProfileAndDoor(props: {
    elev: ElevationApp;
    door: any;
    profile: TProfile;
    side: "left" | "right";
    direction: "in" | "out" | "both";
    // override the jamb/stop clearances between the cell edge and the profile's
    // rabbet. Doors sit off the stop by these gaps; a lite/panel is glazed straight
    // into the rabbet, so the top-down run passes {jamb: 0, stop: 0} for those.
    gaps?: {jamb: number; stop: number};
    // the mounting rabbet to pin the cell edge to; defaults to the profile's primary.
    // balancedMountAnchors picks the secondary rabbet on a dual-mount profile (STD).
    anchor?: FrmRefPoint;
}) {
    const jambGap = props.gaps ? props.gaps.jamb : props.elev.frameSpec.frameJambToDoorGap.get();
    const stopGap = props.gaps ? props.gaps.stop : props.elev.frameSpec.frameStopToDoorGap.get();
    const anchor = props.anchor ?? props.profile.primaryDoorAnchor;

    if (props.side === "left") {
        if(props.direction === "in") {
            props.door.bottomLeft.linkTo(anchor, jambGap, -stopGap);
        } else {
            props.door.topLeft.linkTo(anchor, jambGap, stopGap);
        }
    } else {
        if(props.direction === "in") {
            props.door.bottomRight.linkTo(anchor, -jambGap, -stopGap);
        } else {
            props.door.topRight.linkTo(anchor, -jambGap, stopGap);
        }
    }
}

// the mounting rabbets a profile offers, primary first. A symmetric double-rabbet
// profile (STD) also exposes a secondary rabbet, so an item can mount on either side of
// the centre stop; every other profile offers only its primary.
export function mountAnchors(profile: TProfile): FrmRefPoint[] {
    return profile.secondaryDoorAnchor
        ? [profile.primaryDoorAnchor, profile.secondaryDoorAnchor]
        : [profile.primaryDoorAnchor];
}

// a rabbet's depth from its own profile's face, in raw shape units (post-done(), so
// dimension-accurate). invertY bakes its sign into the point coords, so this is the
// profile's own signed depth — two profiles that share vertical orientation (STD beside
// any non-DE/DW/SL/SR profile) compare directly.
function anchorDepth(profile: TProfile, anchor: FrmRefPoint): number {
    return anchor.getLocalY() - profile.faceAnchor.getLocalY();
}

// which of a double-rabbet profile's two rabbets each face glazes into, as indices
// into the anchors ordered down the section (0 = nearer the top). The section draws
// locationTwo at its top and locationOne at its bottom (see TopDownScene's labels),
// and the outside face is locationOne. Swap these two to swap the convention — it's
// the only place the section's inside/outside orientation is decided.
const OUTSIDE_RABBET = 1;
const INSIDE_RABBET = 0;

// the rabbet a glazed piece mounts in on one flanking profile, and the direction to
// pin it with. A "profile" side — and any profile with a single rabbet (CO, SR, and
// every non-STD) — has no choice: it takes the primary, and a null direction leaves
// the run's swing to place it, which is what those profiles' own invertY conventions
// are tuned for.
//
// A double-rabbet profile is symmetric about the centre stop, so its direction is
// read straight off the geometry instead. Ordering the anchors by local y orders
// their rabbets down the section (invertY bakes its sign into the coords, so this
// follows the profile however it's drawn). Each anchor sits at its rabbet's stop
// corner, so the upper rabbet's pane hangs above its anchor — pinned by its bottom
// edge, linkProfileAndDoor's "in" — and the lower rabbet's below it, pinned by its
// top edge ("out"). Deriving it this way rather than flipping the door's swing keeps
// both rabbets right whatever the swing is, including a no-swing "both".
export function faceMountAnchor(profile: TProfile, side: GlazeSide): {anchor: FrmRefPoint; direction: "in" | "out" | null} {
    const anchors = mountAnchors(profile);
    if(side === "profile" || anchors.length < 2) return {anchor: anchors[0], direction: null};

    const ordered = [...anchors].sort((a, b) => a.getLocalY() - b.getLocalY());
    const rabbet = side === "outside" ? OUTSIDE_RABBET : INSIDE_RABBET;
    return {anchor: ordered[rabbet], direction: rabbetDirection(rabbet)};
}

// the direction linkProfileAndDoor pins with to land a piece in the upper (0) or
// lower (1) rabbet: "in" hangs the piece above its anchor, "out" below.
function rabbetDirection(rabbet: number): "in" | "out" {
    return rabbet === 0 ? "in" : "out";
}

// the direction that lands a piece on the inside / outside face.
export function faceDirection(face: "inside" | "outside"): "in" | "out" {
    return rabbetDirection(face === "outside" ? OUTSIDE_RABBET : INSIDE_RABBET);
}

// the corner linkProfileAndDoor pins for a direction — "both" pins like "out".
export function pinDirection(direction: "in" | "out" | "both"): "in" | "out" {
    return direction === "in" ? "in" : "out";
}

// the direction a profile pins with once it's drawn flipped (getProfileObj's flipY):
// its rabbet now opens the other way, so the piece hangs off the other corner.
export function flippedDirection(direction: "in" | "out" | "both"): "in" | "out" {
    return direction === "in" ? "out" : "in";
}

// a profile's primary rabbet depth from its top outer face, in local units. faceAnchor
// marks the profile's own top face, which a flipped drawing puts at the bottom — the
// top face then sits a jamb depth above it (the faces are dimensioned that far apart).
export function anchorDepthFromTop(profile: TProfile, jambDepth: number): number {
    const depth = anchorDepth(profile, profile.primaryDoorAnchor);
    return depth >= 0 ? depth : depth + jambDepth;
}

// the outer ends of a profile's two faces, in its own local coords: `top` is the face
// drawn at the top of the section, `bottom` the one at the bottom. faceAnchor marks the
// profile's own top face, which a flipped drawing puts at the bottom; the other face's
// end sits a jamb depth straight across the section.
export function faceOuterEnds(profile: TProfile, jambDepth: number): {top: Pt; bottom: Pt} {
    const fa = {x: profile.faceAnchor.getLocalX(), y: profile.faceAnchor.getLocalY()};
    const flipped = anchorDepth(profile, profile.primaryDoorAnchor) < 0;
    return flipped
        ? {top: {x: fa.x, y: fa.y - jambDepth}, bottom: fa}
        : {top: fa, bottom: {x: fa.x, y: fa.y + jambDepth}};
}

type Pt = {x: number; y: number};

// choose the left/right mounting rabbets that keep an item square: the pair whose depths
// from each face match most closely, so both edges pin at the same depth and the flanking
// faces stay coplanar. Only a dual-mount profile (STD) offers a choice; a single-anchor
// side always uses its primary, and a mismatched-orientation neighbour (which can't be
// balanced) keeps the primary too.
export function balancedMountAnchors(left: TProfile, right: TProfile): {left: FrmRefPoint; right: FrmRefPoint} {
    const lefts = mountAnchors(left);
    const rights = mountAnchors(right);

    let best = {left: lefts[0], right: rights[0], diff: Infinity};
    for(const l of lefts) {
        for(const r of rights) {
            const diff = Math.abs(anchorDepth(left, l) - anchorDepth(right, r));
            if(diff < best.diff) best = {left: l, right: r, diff};
        }
    }
    return {left: best.left, right: best.right};
}


// pairHandingMap resolves a pair handing -> [activeLeaf, inactiveLeaf] single-leaf
// handing. Ported from PairHandingMap in core/opening/topening/handing.go, based
// on tyler's definitions. see also: pages/project/quote/openings/QuoteOpenings.tsx
const pairHandingMap: Partial<Record<MdDoorHanding, [SimplifiedHanding, SimplifiedHanding]>> = {
    "LHA": ["LH", "RH"],
    "RHA": ["RH", "LH"],
    "LHA/RHA": ["LH", "RH"],
    "LHRA": ["LHR", "RHR"],
    "RHRA": ["RHR", "LHR"],
    "LHRA/RHRA": ["LHR", "RHR"],
    "LHA-DA": ["LH", "LH"],
    "RHA-DA": ["RH", "RH"],
    "LHA/RHA-DA": ["LH", "RH"],
    "L-LHR": ["LHR", "LHR"],
    "R-RHR": ["RHR", "RHR"],
    "DBL BIFOLD": ["LH", "RH"],
    "RHRH-COMM": ["RH", "RH"],
    "LHLH-COMM": ["LH", "LH"],
    "LHRH-COMM": ["LH", "RH"],
    "LHR/LHR": ["LHR", "LHR"],
    "RHR/RHR": ["RHR", "RHR"]
};

type SimplifiedHanding = "LH" | "RH" | "LHR" | "RHR";

// handings whose two leaves are both active; flipDoubleActiveHanding swaps which
// leaf the active flag selects. Mirrors BothActiveHandings in topening.
const bothActiveHandings: ReadonlySet<MdDoorHanding> = new Set([
    "LHA/RHA", "LHRA/RHRA", "LHA/RHA-DA",
]);

// simplifyHanding resolves a pair handing into the single-leaf handing for the
// requested leaf (active vs inactive). Non-pair handings, and openings that are
// neither a pair nor communicating, are returned unchanged.
// Ported from GetHanding in core/opening/topening/handing.go.
export function simplifyHanding(props: {
    handing: MdDoorHanding;
    active: boolean;
    isPair: boolean;
    frameProfile: MdFrameProfileType;
    flipDoubleActiveHanding?: boolean;
}): SimplifiedHanding {
    let handing = props.handing;

    if (props.isPair || props.frameProfile === "COM") {
        const pair = pairHandingMap[handing];
        if (pair) {
            const flip = bothActiveHandings.has(handing) && !!props.flipDoubleActiveHanding;
            const active = flip ? !props.active : props.active;
            handing = active ? pair[0] : pair[1];
        }
    }

    const pair = pairHandingMap[handing];
    if (pair) {
        return props.active ? pair[0] : pair[1];
    }

    return handing as SimplifiedHanding;
}

// specialty doors slide/fold/pocket rather than swing on a hinge
const noSwingHandings: ReadonlySet<MdDoorHanding> = new Set([
    "POCKET", "BYPASS", "BIFOLD", "CO",
    "LH-POCKET", "RH-POCKET", "LH-SLIDE", "RH-SLIDE",
    "LH-BIFOLD", "RH-BIFOLD", "LH-BARN", "RH-BARN",
    "DBL POCKET", "DBL BYPASS", "DBL BIFOLD",
]);

// which jamb the door is hinged on (and which way it swings). `active` selects
// the leaf: the active (left) leaf hinges left and swings "in", the inactive
// (right) leaf hinges right and swings "out" — matching the LH (active) / RH
// (inactive) convention. A handing throws if it has no leaf on the requested
// side.
export function getSwingSide(app: ElevationApp, active: boolean): {
    side: "left" | "right";
    direction: "out" | "in" | "both"
} {
    return swingSideFor(app.frameSpec, active);
}

// which edge the hinges sit on for a leaf. Falls back to the active-leaf convention (active
// hinges left, inactive right) for handings with no swing so the views still render.
export function hingeSideOrDefault(app: ElevationApp, active: boolean): "left" | "right" {
    try {
        return getSwingSide(app, active).side;
    } catch {
        return active ? "left" : "right";
    }
}

// getSwingSide for a given frame spec, e.g. a secondary door slot's own (multi-opening screens).
export function swingSideFor(frame: MdFrameSpec, active: boolean): {
    side: "left" | "right";
    direction: "out" | "in" | "both"
} {
    let rawHanding = frame.handing as MdDoorHanding;
    if(noSwingHandings.has(rawHanding)) {
        throw new Error("door handing has no hinge swing: " + rawHanding);
    }

    const doubleActing = rawHanding.endsWith("-DA");
    if(doubleActing) {
        rawHanding = rawHanding.replace(/-DA$/, "") as any;
    }

    switch (rawHanding) {
        case "L-LHR":
        case "LHR/LHR":
            return {
                side: active ? "right" : "left",
                direction: active ? "in" : "out",
            }
        case "R-RHR":
        case "RHR/RHR":
            return {
                side: active ? "left" : "right",
                direction: active ? "in" : "out",
            }

    }

    let handing = simplifyHanding({
        handing: rawHanding,
        active: active,
        isPair: frame.isPair,
        frameProfile: frame.profile,
        flipDoubleActiveHanding: frame.flipDoubleActiveHanding,
    });

    switch(handing) {
        case "LH":
            return {
                side: "left",
                direction: doubleActing ? "both" : "in",
            }
        case "RH":
            return {
                side: "right",
                direction: doubleActing ? "both" : "in",
            }
        case "LHR":
            return {
                side: "left",
                direction: doubleActing ? "both" : "out",
            }
        case "RHR":
            return {
                side: "right",
                direction: doubleActing ? "both" : "out",
            }
        default:
            throw new Error("unknown handing: " + handing);
    }
}

export function getGrabDetail(elev: ElevationApp, active: boolean) {
    return {
        items: [
            {
                top: 24 * inches,
                inset: 1 * inches,
                kind: "knob",
            }
        ]
    }
}